import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";

const LOCALES = ["en", "es", "nl"];
const RELATION_KEYS = ["goals", "muscles", "body_systems", "sports", "recipes", "exercises", "articles", "products", "categories", "tags"];

function slugify(s) {
  return (s || "").toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");
}

const emptyTr = () => ({ title: "", excerpt: "", body: "", seo_title: "", meta_description: "", faq: [] });

export default function AdminContentEditor() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [allContent, setAllContent] = useState([]);
  const [busy, setBusy] = useState(false);

  const [type, setType] = useState("article");
  const [section, setSection] = useState("");
  const [status, setStatus] = useState("draft");
  const [image, setImage] = useState("");
  const [topics, setTopics] = useState("");
  const [slugs, setSlugs] = useState({ en: "", es: "", nl: "" });
  const [translations, setTranslations] = useState({ en: emptyTr(), es: emptyTr(), nl: emptyTr() });
  const [relations, setRelations] = useState(Object.fromEntries(RELATION_KEYS.map((k) => [k, []])));
  const [dataJson, setDataJson] = useState("{}");
  const [authors, setAuthors] = useState([]);
  const [authorId, setAuthorId] = useState("");
  const [reviewerId, setReviewerId] = useState("");
  const [isFeatured, setIsFeatured] = useState(false);
  const [healthDisclaimer, setHealthDisclaimer] = useState(false);
  const [lastReviewed, setLastReviewed] = useState("");
  const [externalKey, setExternalKey] = useState("");

  useEffect(() => {
    api.get("/api/admin/meta").then(({ data }) => setMeta(data)).catch(() => {});
    api.get("/api/admin/content?limit=100").then(({ data }) => setAllContent(data.items)).catch(() => {});
    api.get("/api/admin/authors").then(({ data }) => setAuthors(data)).catch(() => {});
    if (isEdit) {
      api.get(`/api/admin/content/${id}`).then(({ data }) => {
        setType(data.type); setSection(data.section || ""); setStatus(data.status);
        setImage(data.featured_image_url || ""); setTopics((data.topics || []).join(", "));
        setSlugs({ en: "", es: "", nl: "", ...(data.slugs || {}) });
        setAuthorId(data.author_id || ""); setReviewerId(data.reviewer_id || "");
        setIsFeatured(!!data.is_featured); setHealthDisclaimer(!!data.health_disclaimer_applicable);
        setLastReviewed(data.last_reviewed_at || ""); setExternalKey(data.external_key || "");
        const tr = { en: emptyTr(), es: emptyTr(), nl: emptyTr() };
        Object.entries(data.translations || {}).forEach(([loc, v]) => { tr[loc] = { ...emptyTr(), ...v }; });
        setTranslations(tr);
        const rel = Object.fromEntries(RELATION_KEYS.map((k) => [k, (data.relations || {})[k] || []]));
        setRelations(rel);
        setDataJson(JSON.stringify(data.data || {}, null, 2));
      }).catch(() => toast.error("Failed to load"));
    }
  }, [id, isEdit]);

  const sectionsForType = useMemo(
    () => (meta?.sections || []).filter((s) => s.type === type),
    [meta, type]
  );

  function updateTr(loc, field, value) {
    setTranslations((prev) => ({ ...prev, [loc]: { ...prev[loc], [field]: value } }));
  }

  async function save() {
    setBusy(true);
    let parsedData = {};
    try { parsedData = JSON.parse(dataJson || "{}"); }
    catch (e) { toast.error("Type-specific data is not valid JSON"); setBusy(false); return; }

    // Only send translations that have a title.
    const cleanTr = {};
    LOCALES.forEach((loc) => { if (translations[loc]?.title?.trim()) cleanTr[loc] = translations[loc]; });
    const cleanSlugs = {};
    LOCALES.forEach((loc) => {
      if (translations[loc]?.title?.trim()) {
        cleanSlugs[loc] = slugify(slugs[loc]?.trim() || translations[loc].title);
      }
    });

    const payload = {
      type, section: section || null, status,
      featured_image_url: image,
      topics: topics.split(",").map((x) => x.trim()).filter(Boolean),
      slugs: cleanSlugs, translations: cleanTr, relations, data: parsedData,
      author_id: authorId || null, reviewer_id: reviewerId || null,
      is_featured: isFeatured, health_disclaimer_applicable: healthDisclaimer,
      last_reviewed_at: lastReviewed || null,
    };

    try {
      if (isEdit) await api.put(`/api/admin/content/${id}`, payload);
      else await api.post("/api/admin/content", payload);
      toast.success("Saved");
      navigate("/admin/content");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="mx-auto max-w-4xl">
      <Link to="/admin/content" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft className="h-4 w-4" />Back to content</Link>
      <div className="mt-3 flex items-center justify-between">
        <h1 className="font-heading text-2xl font-extrabold text-slate-900">{isEdit ? "Edit content" : "New content"}</h1>
        <Button onClick={save} disabled={busy} data-testid="admin-save-content" className="rounded-full bg-emerald-600 hover:bg-emerald-700"><Save className="mr-1.5 h-4 w-4" />{busy ? "Saving…" : "Save"}</Button>
      </div>

      {/* Base fields */}
      <div className="mt-6 grid gap-4 rounded-2xl border border-slate-200 bg-white p-6 sm:grid-cols-2">
        <div>
          <Label>Type</Label>
          <select value={type} onChange={(e) => { setType(e.target.value); setSection(""); }} data-testid="editor-type" className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2 text-sm">
            {meta?.content_types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <Label>Section</Label>
          <select value={section} onChange={(e) => setSection(e.target.value)} data-testid="editor-section" className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2 text-sm">
            <option value="">(auto)</option>
            {(meta?.sections || []).map((s) => <option key={s.slug} value={s.slug}>{s.slug}</option>)}
          </select>
        </div>
        <div>
          <Label>Status</Label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="editor-status" className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2 text-sm">
            {meta?.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <Label>Topics (comma-separated)</Label>
          <Input value={topics} onChange={(e) => setTopics(e.target.value)} placeholder="nutrition, sleep" className="mt-1.5" data-testid="editor-topics" />
        </div>
        <div className="sm:col-span-2">
          <Label>Featured image URL</Label>
          <Input value={image} onChange={(e) => setImage(e.target.value)} placeholder="https://…" className="mt-1.5" data-testid="editor-image" />
        </div>
      </div>

      {/* Editorial trust */}
      <div className="mt-6 grid gap-4 rounded-2xl border border-slate-200 bg-white p-6 sm:grid-cols-2">
        <div className="sm:col-span-2"><h2 className="font-heading text-lg font-bold text-slate-900">Editorial & trust</h2></div>
        <div>
          <Label>Author</Label>
          <select value={authorId} onChange={(e) => setAuthorId(e.target.value)} data-testid="editor-author" className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2 text-sm">
            <option value="">(none)</option>
            {authors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        <div>
          <Label>Reviewer (only if a real reviewer exists)</Label>
          <select value={reviewerId} onChange={(e) => setReviewerId(e.target.value)} data-testid="editor-reviewer" className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2 text-sm">
            <option value="">(none)</option>
            {authors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        <div>
          <Label>Last reviewed (ISO date)</Label>
          <Input value={lastReviewed} onChange={(e) => setLastReviewed(e.target.value)} placeholder="2026-06-01" className="mt-1.5" data-testid="editor-last-reviewed" />
        </div>
        <div className="flex items-end gap-6">
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={isFeatured} onChange={(e) => setIsFeatured(e.target.checked)} data-testid="editor-featured" />Featured</label>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={healthDisclaimer} onChange={(e) => setHealthDisclaimer(e.target.checked)} data-testid="editor-health-disclaimer" />Health disclaimer</label>
        </div>
        {externalKey && <div className="sm:col-span-2 text-xs text-slate-400">Imported item — external_key: <code className="rounded bg-slate-100 px-1">{externalKey}</code></div>}
      </div>

      {/* Translations */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="font-heading text-lg font-bold text-slate-900">Translations</h2>
        <Tabs defaultValue="en" className="mt-4">
          <TabsList>{LOCALES.map((loc) => <TabsTrigger key={loc} value={loc} data-testid={`tab-${loc}`}>{loc.toUpperCase()}</TabsTrigger>)}</TabsList>
          {LOCALES.map((loc) => (
            <TabsContent key={loc} value={loc} className="mt-4 space-y-4">
              <div>
                <Label>Title</Label>
                <Input value={translations[loc].title}
                  onChange={(e) => updateTr(loc, "title", e.target.value)}
                  className="mt-1.5" data-testid={`title-${loc}`} />
              </div>
              <div>
                <Label>Slug</Label>
                <Input value={slugs[loc]} onChange={(e) => setSlugs((s) => ({ ...s, [loc]: e.target.value }))} className="mt-1.5" data-testid={`slug-${loc}`} />
              </div>
              <div>
                <Label>Excerpt</Label>
                <Textarea value={translations[loc].excerpt} onChange={(e) => updateTr(loc, "excerpt", e.target.value)} rows={2} className="mt-1.5" data-testid={`excerpt-${loc}`} />
              </div>
              <div>
                <Label>Body (HTML)</Label>
                <Textarea value={translations[loc].body} onChange={(e) => updateTr(loc, "body", e.target.value)} rows={6} className="mt-1.5 font-mono text-xs" data-testid={`body-${loc}`} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><Label>SEO title</Label><Input value={translations[loc].seo_title} onChange={(e) => updateTr(loc, "seo_title", e.target.value)} className="mt-1.5" /></div>
                <div><Label>Meta description</Label><Input value={translations[loc].meta_description} onChange={(e) => updateTr(loc, "meta_description", e.target.value)} className="mt-1.5" /></div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Relations */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="font-heading text-lg font-bold text-slate-900">Relationships</h2>
        <p className="text-sm text-slate-500">Link this item to others (hold Ctrl/Cmd to select multiple).</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {RELATION_KEYS.map((key) => (
            <div key={key}>
              <Label className="capitalize">{key.replace("_", " ")}</Label>
              <select multiple value={relations[key]} data-testid={`rel-${key}`}
                onChange={(e) => setRelations((r) => ({ ...r, [key]: Array.from(e.target.selectedOptions).map((o) => o.value) }))}
                className="mt-1.5 h-24 w-full rounded-md border border-slate-200 p-2 text-xs">
                {allContent.filter((c) => c.id !== id).map((c) => <option key={c.id} value={c.id}>{`${c.title} (${c.type})`}</option>)}
              </select>
            </div>
          ))}
        </div>
      </div>

      {/* Type-specific data */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="font-heading text-lg font-bold text-slate-900">Type-specific data (JSON)</h2>
        <p className="text-sm text-slate-500">E.g. recipe ingredients/instructions, exercise difficulty, product offers.</p>
        <Textarea value={dataJson} onChange={(e) => setDataJson(e.target.value)} rows={8} className="mt-3 font-mono text-xs" data-testid="editor-data-json" />
      </div>
    </div>
  );
}
